import { useCallback, useEffect, useState } from 'react'
import { User } from '../types'
import { auditService } from '../services/auditService'
import { loadHeldRuns, releaseHold, rejectRun, HeldRun, HOLD_STAGE_LABEL } from '../services/paymentHoldService'
import './PaymentHoldControls.css'

/**
 * Where held and rejected payment runs are visible.
 *
 * Without this a held run would simply disappear from both approval queues —
 * exactly the "stuck forever with nothing on the record" problem that holding
 * was meant to fix.
 */
export default function HeldPaymentRuns({ user, onChanged }: { user: User; onChanged?: () => void }) {
  const [rows, setRows] = useState<HeldRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<HeldRun | null>(null)
  const [reason, setReason] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await loadHeldRuns())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load held runs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const money = (n: number) => `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-AU') : '—')

  const handleRelease = async (row: HeldRun) => {
    setBusyId(row.id)
    try {
      await releaseHold(row.id)
      await auditService.log(user, 'payment_run_hold_released', { details: { payment_run: row.id } })
      await load()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not release that hold')
    } finally {
      setBusyId(null)
    }
  }

  const handleReject = async () => {
    if (!rejecting || !reason.trim()) return
    setBusyId(rejecting.id)
    try {
      await rejectRun(rejecting.id, reason, user.id)
      await auditService.log(user, 'payment_run_rejected', {
        details: { payment_run: rejecting.id, reason: reason.trim(), from: 'held_list' },
      })
      setRejecting(null)
      setReason('')
      await load()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reject that run')
    } finally {
      setBusyId(null)
    }
  }

  if (loading) return null
  if (error) {
    return (
      <div className="held-panel">
        <h3>Held runs</h3>
        <div className="phc-error">
          {error} <button className="phc-btn" onClick={load}>Retry</button>
        </div>
      </div>
    )
  }
  if (rows.length === 0) return null

  return (
    <div className="held-panel">
      <h3>Held &amp; rejected runs ({rows.length})</h3>
      <p>Runs an approver stopped. Releasing one puts it back into the queue it came from.</p>

      {rows.map((r) => (
        <div className="held-row" key={r.id}>
          <div className="held-main">
            <div className="held-title">
              {r.vehicle} · {money(r.totalAmount)}
              <span className={`held-badge ${r.status === 'rejected' ? 'rejected' : ''}`}>
                {r.status === 'rejected' ? 'Rejected' : 'On hold'}
              </span>
            </div>
            <div className="held-meta">
              {r.sellerName} · run {r.invoiceLabel} ·{' '}
              {r.status === 'rejected'
                ? `rejected ${when(r.rejectedAt)}`
                : `held by ${r.heldByName} at ${r.heldAtStage ? HOLD_STAGE_LABEL[r.heldAtStage] : 'approval'} on ${when(r.heldAt)}`}
            </div>
            <div className="held-reason">
              {r.status === 'rejected' ? r.rejectedReason || 'No reason recorded.' : r.holdReason || 'No reason recorded.'}
            </div>
          </div>

          {r.status !== 'rejected' && (
            <div className="held-actions">
              <button className="phc-btn solid" disabled={busyId === r.id} onClick={() => handleRelease(r)}>
                {busyId === r.id ? 'Working…' : 'Release back'}
              </button>
              <button className="phc-btn danger" disabled={busyId === r.id} onClick={() => setRejecting(r)}>
                Reject
              </button>
            </div>
          )}
        </div>
      ))}

      {rejecting && (
        <div className="phc-overlay" role="dialog" onClick={() => setRejecting(null)}>
          <div className="phc-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Reject this payment run</h4>
            <p className="phc-blurb">
              {rejecting.vehicle} · {money(rejecting.totalAmount)}. The run closes permanently; a new one
              has to be initiated from the deal workspace if the payment should still happen.
            </p>
            <label className="phc-field">
              <span>Reason (required)</span>
              <textarea rows={3} autoFocus value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <div className="phc-actions">
              <button className="phc-btn" onClick={() => setRejecting(null)} disabled={busyId === rejecting.id}>
                Cancel
              </button>
              <button
                className="phc-btn danger solid"
                onClick={handleReject}
                disabled={!reason.trim() || busyId === rejecting.id}
              >
                {busyId === rejecting.id ? 'Rejecting…' : 'Reject it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
